'use client';

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import themes, { defaultTheme, type Theme } from '@/lib/themes';
import { themeCssVars, THEME_STORAGE_KEY } from '@/lib/theme-vars';
import patterns, { type Pattern } from '@/lib/patterns';

interface ThemeContextValue {
  theme: Theme;
  setThemeById: (id: string) => void;
  allThemes: Theme[];
  pattern: Pattern;
  setPatternById: (id: string) => void;
  allPatterns: Pattern[];
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  setThemeById: () => {},
  allThemes: themes,
  pattern: patterns[0],
  setPatternById: () => {},
  allPatterns: patterns,
});

export function useTheme() {
  return useContext(ThemeContext);
}

function applyThemeToDOM(theme: Theme) {
  const root = document.documentElement;
  // Property list lives in theme-vars.ts and is shared with the pre-paint <head>
  // script in the root layout, so the two can never drift out of sync.
  for (const [name, value] of themeCssVars(theme)) {
    root.style.setProperty(name, value);
  }
}

export default function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [pattern, setPattern] = useState<Pattern>(patterns[0]);

  useEffect(() => {
    // Restore theme from localStorage after hydration to avoid SSR mismatch
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const found = themes.find((t) => t.id === parsed.id);
        if (found) {
          setTheme(found);
          return; // applyThemeToDOM will be called by the theme-change effect below
        }
      }
    } catch {}
    applyThemeToDOM(defaultTheme);
  }, []);

  useEffect(() => {
    applyThemeToDOM(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    } catch {}
  }, [theme]);

  const setThemeById = (id: string) => {
    const found = themes.find((t) => t.id === id);
    if (found) setTheme(found);
  };

  const setPatternById = (id: string) => {
    const found = patterns.find((p) => p.id === id);
    if (found) setPattern(found);
  };

  return (
    <ThemeContext.Provider value={{ theme, setThemeById, allThemes: themes, pattern, setPatternById, allPatterns: patterns }}>
      {children}
    </ThemeContext.Provider>
  );
}
